import { useEffect, useState } from "react";
import { ArrowRight, Check, Search, Sparkles, ShoppingBag, Target } from "lucide-react";

/**
 * ExampleInteraction — a scripted command → result flow.
 * Shows a real use case ("Find a black hoodie under £50") with
 * a 4-step reveal animation and a mini product-result mock.
 */
const steps = [
  { icon: Sparkles, label: "Understands the request", detail: "intent: find_product · category: hoodie · color: black · price: < £50" },
  { icon: Search, label: "Finds matching items", detail: "scanning catalog · 6 results · ranked by fit" },
  { icon: Target, label: "Selects the best option", detail: "chosen: Heavyweight Hoodie · £42 · size M" },
  { icon: ShoppingBag, label: "Adds it to cart", detail: "cart +1 · ready to checkout · confirmed aloud" },
];

const ExampleInteraction = () => {
  const [active, setActive] = useState(0);
  const [cycleKey, setCycleKey] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((s) => {
        if (s >= steps.length - 1) {
          setCycleKey((k) => k + 1);
          return 0;
        }
        return s + 1;
      });
    }, 1600);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="example"
      data-testid="example-interaction-section"
      className="relative py-28 md:py-36 border-t border-white/[0.06] bg-[#070707]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col items-start md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
              <span className="font-mono uppercase tracking-[0.2em] text-[10.5px] text-zinc-400">
                A real interaction
              </span>
            </div>
            <h2 className="mt-6 font-display font-semibold tracking-tight text-white text-3xl md:text-4xl lg:text-5xl text-balance">
              One sentence. Four quiet moves. Done.
            </h2>
          </div>
          <p className="font-body text-zinc-400 text-base md:text-lg max-w-md leading-relaxed">
            Watch EqualLayer interpret a natural request and complete it end-to-end — without the user
            touching the page.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-[1fr_1.05fr] gap-10 items-stretch">
          {/* Left — command + steps */}
          <div className="rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 p-6 md:p-8">
            {/* User command */}
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" />
              User
            </div>
            <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 font-body text-[15px] md:text-base text-white">
              “Find a black hoodie under{" "}
              <span className="text-indigo-300">£50</span>.”
            </div>

            {/* Equal label */}
            <div className="mt-7 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />
              Equal
            </div>

            {/* Steps */}
            <ol key={cycleKey} className="mt-3 space-y-2.5">
              {steps.map((s, i) => {
                const Icon = s.icon;
                const state = i < active ? "done" : i === active ? "active" : "idle";
                return (
                  <li
                    key={i}
                    data-testid={`example-step-${i}`}
                    className={`relative flex items-start gap-4 rounded-lg border px-4 py-3.5 transition-all ${
                      state === "active"
                        ? "border-indigo-500/40 bg-indigo-500/10 shadow-[0_0_24px_rgba(99,102,241,0.15)]"
                        : state === "done"
                        ? "border-white/[0.06] bg-white/[0.02]"
                        : "border-white/[0.04] bg-transparent opacity-60"
                    }`}
                  >
                    <div
                      className={`mt-0.5 h-8 w-8 rounded-md flex items-center justify-center transition-all ${
                        state === "active"
                          ? "bg-indigo-500/20 text-indigo-300"
                          : state === "done"
                          ? "bg-emerald-500/15 text-emerald-300"
                          : "bg-white/[0.03] text-zinc-500"
                      }`}
                    >
                      {state === "done" ? (
                        <Check className="h-4 w-4" strokeWidth={2} />
                      ) : (
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-body text-[15px] text-white">{s.label}</div>
                        <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-zinc-500">
                          step {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="mt-1 font-mono text-[11px] text-zinc-500">{s.detail}</div>
                    </div>
                    {state === "active" && (
                      <div className="absolute inset-x-0 bottom-0 h-px overflow-hidden rounded-b-lg">
                        <div className="animate-shimmer h-px w-full" />
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Right — result card / mini product view */}
          <div className="relative rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.06] bg-white/[0.02]">
              <div className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-full bg-[#ff5f57]/70" />
                <span className="h-3 w-3 rounded-full bg-[#febc2e]/70" />
                <span className="h-3 w-3 rounded-full bg-[#28c840]/70" />
              </div>
              <div className="ml-3 font-mono text-[11px] text-zinc-500">shop.example.com / hoodies</div>
              <div className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-indigo-300">
                Equal · active
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                3 matches · under £50
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3">
                {[
                  { name: "Classic Zip Hoodie", price: "£48", selected: false },
                  { name: "Heavyweight Hoodie", price: "£42", selected: true },
                  { name: "Relaxed Pullover", price: "£45", selected: false },
                ].map((p, i) => (
                  <div
                    key={i}
                    data-testid={`result-card-${i}`}
                    className={`relative rounded-xl border p-3 transition-all ${
                      p.selected
                        ? "border-indigo-500/50 bg-indigo-500/[0.07] shadow-[0_0_30px_rgba(99,102,241,0.2)]"
                        : "border-white/[0.06] bg-white/[0.015]"
                    }`}
                  >
                    <div className="aspect-[4/5] rounded-md bg-gradient-to-br from-zinc-800 to-zinc-950 relative overflow-hidden">
                      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(99,102,241,0.12),transparent_60%)]" />
                      {p.selected && (
                        <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-indigo-500 flex items-center justify-center shadow-[0_0_12px_rgba(99,102,241,0.8)]">
                          <Check className="h-3 w-3 text-white" strokeWidth={2.5} />
                        </div>
                      )}
                    </div>
                    <div className="mt-3 font-body text-xs text-white leading-tight">{p.name}</div>
                    <div className="mt-0.5 font-mono text-[11px] text-zinc-400">{p.price}</div>
                  </div>
                ))}
              </div>

              {/* Result bar */}
              <div className="mt-6 rounded-xl border border-white/[0.06] bg-[#050505] p-4 md:p-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                      <ShoppingBag className="h-4 w-4 text-emerald-300" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-body text-[14px] text-white">
                        Added <span className="text-indigo-300">Heavyweight Hoodie</span> · Size M
                      </div>
                      <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
                        cart total · £42 · ready to checkout
                      </div>
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300">
                    done
                    <ArrowRight className="h-3 w-3" strokeWidth={2} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExampleInteraction;
